/**
 * The CRM's Webwin theme.
 *
 * Extends `BaseTheme` rather than `ModernTheme`, which is the only option: ModernTheme takes a
 * variant string and hard-codes both of its palettes, so there is nowhere to put brand blue.
 * `derivePalette` expands twelve seed colours into the forty-odd slots Webwin actually paints with
 * (hover and pressed ramps, disabled text, selection, scroll thumbs, bevels), which is the work this
 * app does not want to do by hand.
 *
 * WHY A THEME AND NOT PER-CONTROL COLOURS. Webwin's `Button` has no background option at all, and
 * neither do `TextBox`, `ListBox` or `DataGrid` - their chrome comes from the theme. So the only way
 * for a CRM button to be brand blue is for the theme's accent to be brand blue. That is the same
 * constraint the OpenSilver app met from the other direction, where every button carried an explicit
 * Background because the control template would not.
 *
 * WHY `crm` TOKENS HANG OFF IT. Everything Webwin does not know about - a status chip, a sparkline
 * series, the dark navigation rail - still has to follow the theme, and a control painting itself
 * gets handed the `Theme` and nothing else. Reaching those colours through the theme it is already
 * holding is what keeps them changing with it; a module-level palette read once at import would not.
 */
import {
  BaseTheme,
  derivePalette,
  fade,
  type Color,
  type RenderContext,
  type Rect,
  type Theme,
  type ThemeMetrics,
  type VisualState
} from 'webwin';
import { crmColors, ON_BRAND, type CrmColors } from './palette.js';

const UI_FONT = "'Segoe UI Variable Text', 'Segoe UI', system-ui, -apple-system, sans-serif";
const MONO_FONT = "'Cascadia Mono', Consolas, 'Courier New', monospace";

export class CrmTheme extends BaseTheme {
  /** The CRM's own colours, for everything Webwin has no slot for. */
  readonly crm: CrmColors;

  constructor(dark: boolean) {
    const c = crmColors(dark);
    super({
      name: dark ? 'crm-dark' : 'crm-light',
      isDark: dark,
      palette: derivePalette({
        // A page is drawn on `Page`, which takes its background from windowBg - so `desktop` is
        // only ever seen behind a dialog, and matching the page there is what keeps a modal from
        // sitting on a stripe of some other colour.
        desktop: c.pageBackground,
        surface: c.card,
        // Buttons and fields are card-coloured too. The CRM's cards ARE the surface, and a raised
        // ramp derived from a different base makes every button read as a slightly wrong white.
        raised: c.card,
        field: c.card,
        ink: c.text,
        accent: c.brand,
        accentText: ON_BRAND,
        border: c.border,
        titleBar: c.card,
        titleText: c.text,
        isDark: dark,
        // Placed by hand, because a derived ramp gets these wrong in a way that shows:
        overrides: {
          // The page background is a distinct, dimmer surface than a card in this design; derived
          // from `surface` it would come out the same colour and the cards would vanish.
          desktop: c.pageBackground,
          // Secondary and disabled text are measured values from the CRM's own palette, not a
          // percentage of the ink - that is where a derived palette usually fails AA.
          textSubtle: c.mutedText,
          textDisabled: c.mutedText,
          // A row band inside a card.
          headerBg: c.altSurface,
          headerBgHover: c.altSurface,
          headerText: c.subtleText,
          gridLine: c.border,
          // Selection is the brand at low strength rather than solid blue: a selected row in this
          // app is usually a row of text, and solid accent behind it loses the text's own colours.
          selectionBg: fade(c.brand, dark ? 0.32 : 0.14),
          selectionText: c.text,
          selectionInactiveBg: fade(c.brand, dark ? 0.2 : 0.08),
          fieldBorderFocused: c.brand,
          focusRing: c.brand
        }
      }),
      metrics: METRICS
    });
    this.crm = c;
  }

  /**
   * Modern-style themes underline a focused field with the accent colour instead of ringing it. The
   * CRM's fields are dense - a form is often a grid of them - and a 2px ring around each one reads
   * as a validation error.
   */
  protected override get focusUnderline(): boolean {
    return true;
  }

  /**
   * A non-default button is a card-coloured rectangle with a border, which in this palette makes it
   * nearly invisible against the card it sits on. Hovering it deserves more than the derived ramp's
   * two percent.
   */
  protected override buttonFill(s: VisualState): Color {
    if (s.disabled !== true && s.isDefault !== true && (s.hovered === true || s.pressed === true)) {
      return this.crm.altSurface;
    }
    return super.buttonFill(s);
  }

  protected override decorateButton(_rc: RenderContext, _r: Rect, _s: VisualState): void {
    // Nothing yet. Declared so the one place a CRM button could grow a flourish is obvious.
  }
}

/**
 * The CRM colours off whatever theme a control was handed.
 *
 * `onPaint` receives a `Theme`, not a `CrmTheme`, so every custom control needs this one narrowing.
 * It falls back to the light palette rather than throwing: a control that paints slightly wrong is a
 * better failure than a desktop that will not render, and the only way to get here is a Webwin
 * dialog drawn with a stock theme.
 */
export function crm(theme: Theme): CrmColors {
  return theme instanceof CrmTheme ? theme.crm : FALLBACK;
}

const FALLBACK = crmColors(false);

/**
 * Metrics, where they differ from Webwin's defaults.
 *
 * `controlHeight` 30 and the font size match what the OpenSilver views asked for, so a form ported
 * across keeps its proportions. `buttonMinWidth` drops from 86 to 0 because this app has rows of
 * small buttons - Open, Edit, Restore - and an 86px floor would make a three-button row wider than
 * the card it sits in at phone width.
 */
const METRICS: Partial<ThemeMetrics> = {
  controlCornerRadius: 4,
  controlHeight: 30,
  buttonMinWidth: 0,
  windowCornerRadius: 8,
  listItemHeight: 28,
  font: { family: UI_FONT, size: 13 },
  captionFont: { family: UI_FONT, size: 13, weight: 600 },
  monoFont: { family: MONO_FONT, size: 12.5 }
};
